import type { MouseEvent } from "react";
import type { NavGroup } from "../features/registry";

interface NavTabsProps {
  groups: NavGroup[];
  activeGroupId: string | null;
  onNavigate: (path: string) => void;
}

/**
 * The desktop-width horizontal nav — an inline alternative to opening the
 * drawer, shown at >=1024px alongside it (see `.nav-tabs`/`.nav-drawer__toggle`
 * in app.css, which hide whichever of the two doesn't apply at the current
 * width). Duplicates the drawer's `aria-label="Site navigation"`; that's
 * only safe because the two are never both visible at once — see Layout.tsx.
 *
 * Labels only, unlike the drawer: the header row also holds the league
 * badge lockup, and with the icons it no longer fits the 880px desktop
 * width (they cost ~96px). The badge is the header's visual anchor anyway.
 * One tab per group, landing on the group's first page; the group's other
 * pages are the section tabs under the header (`SectionTabs`).
 */
export function NavTabs({ groups, activeGroupId, onNavigate }: NavTabsProps) {
  return (
    <nav className="nav-tabs" aria-label="Site navigation">
      <ul className="nav-tabs__list">
        {groups.map((group) => (
          <li key={group.id}>
            {/* A real link (not a button): lets the URL show in the status
                bar on hover, be copied via right-click, and be opened in a
                new tab/window via a modified click — see
                `navigateOnPlainClick` for why only a plain click is
                intercepted for client-side routing. */}
            <a
              href={group.pages[0]!.path}
              className="nav-tabs__item"
              aria-current={group.id === activeGroupId ? "page" : undefined}
              onClick={navigateOnPlainClick(() => onNavigate(group.pages[0]!.path))}
            >
              {group.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * Wraps a nav click handler so it only intercepts (and client-side routes)
 * plain left-clicks — anything modified (ctrl/cmd/shift/alt, or a
 * middle-click) is left alone so the browser's native "open in new
 * tab/window" still works on these `<a>` tags. Shared with NavDrawer.
 */
export function navigateOnPlainClick(
  onSelect: () => void,
): (event: MouseEvent<HTMLAnchorElement>) => void {
  return (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    onSelect();
  };
}
